// krida-auth.js - Authentication and Game Management Module
// ADD Modular SDK Imports
import { initializeApp } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-app.js";
import { 
    getFirestore, 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc, 
    deleteDoc, 
    collection, 
    query, 
    where, 
    getDocs, 
    onSnapshot, 
    serverTimestamp, // Replaces FieldValue.serverTimestamp()
    limit,           // Replaces .limit() method
    CACHE_SIZE_UNLIMITED // Replaces firebase.firestore.CACHE_SIZE_UNLIMITED
    // Add other specific Firestore functions you might need (e.g., addDoc, runTransaction)
} from "https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js";
import { 
    getAuth, 
    GoogleAuthProvider, 
    signInWithPopup, // Changed from signInWithRedirect for simplicity
    signOut as firebaseSignOut, // Alias to avoid conflict if we name our signOutUser "signOut"
    onAuthStateChanged as firebaseOnAuthStateChanged, // Alias
    getRedirectResult, // Still needed if you stick with redirect
    getIdToken
} from "https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js";
import { 
    getFunctions, 
    httpsCallable 
} from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";

console.log("KridaAuth script loaded - Using Modular SDK.");

// --- Module Scoped Variables (including Firebase services and auth state) ---
let app, auth, functions, db;
let firestoreAvailable = false;
let currentUserFromAuth = null; // DECLARE currentUserFromAuth HERE
let currentUserIdFromAuth = null;
let currentGameIdFromAuth = null;
let authInProgress = false;
let firestoreUnsubscribe = null;

// --- Firebase Config & Initialization (as previously defined) ---
const firebaseConfig = {
    apiKey: "AIzaSyBaFKCukaHTcgQ9JLQQIrzQAwjbEXsvo8M",
    authDomain: "kridapatra.firebaseapp.com",
    projectId: "kridapatra",
    storageBucket: "kridapatra.firebasestorage.app",
    messagingSenderId: "879485512647",
    appId: "1:879485512647:web:7430f1da60cb1ecb5807e2",
    measurementId: "G-V721F19M1M"
};

try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    functions = getFunctions(app);
    db = getFirestore(app, "krida-patra");
    firestoreAvailable = true;
    console.log("Firebase services initialization complete.");
    // testConnection(); // Assuming testConnection is defined elsewhere or removed for brevity
} catch (error) {
    console.error("CRITICAL Firebase initialization error:", error);
    firestoreAvailable = false;
    if (window.KridaUI) KridaUI.updateGameMessage("Critical Error", `Cannot connect services: ${error.message}`);
    app = auth = functions = db = null;
}

// --- Helper function for initials (module scope) ---
function getInitials(displayName) {
    if (!displayName || typeof displayName !== 'string') return "?";
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length > 0) return parts[0].substring(0, 2).toUpperCase();
    return "?";
}

// --- Exported Functions FIRST ---
export function getCurrentUser() {
    return currentUserFromAuth;
}

export function initKridaAuth() {
    console.log("KridaAuth: initKridaAuth() called.");
    if (!auth) {
        console.error("KridaAuth cannot be initialized, Firebase auth service failed to load.");
        return;
    }
    // Ensure setupAuthListenerInternal is defined before being called
    setupAuthListenerInternal(); 
}

export function signInWithGoogle() {
    if (authInProgress || !auth) {
        console.log("Auth operation already in progress or auth service not ready");
        return Promise.reject(new Error("Auth busy or not ready"));
    }
    authInProgress = true;
    if (window.KridaUI) KridaUI.updateGameMessage("Redirecting to Google login...", "");
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider)
        .then((result) => {
            console.log("Signed in successfully via popup!", result.user);
            return result.user;
        })
        .catch((error) => {
            console.error("Error during Google sign-in popup:", error.code, error.message);
            if (window.KridaUI) KridaUI.updateGameMessage("Login Failed", error.message);
            throw error;
        })
        .finally(() => {
            authInProgress = false;
        });
}

export function signOutUser() {
    if (authInProgress || !auth) {
        console.log("Auth operation already in progress or auth service not ready");
        return Promise.reject(new Error("Auth busy or not ready"));
    }
    authInProgress = true;
    if (window.KridaUI) KridaUI.updateGameMessage("Signing out...", "");
    return firebaseSignOut(auth).then(() => {
        console.log("Signed out successfully!");
    }).catch((error) => {
        console.error("Error signing out:", error);
        if (window.KridaUI) KridaUI.updateGameMessage("Logout Failed", error.message);
        throw error;
    }).finally(() => {
        authInProgress = false;
    });
}

export const onAuthChanged = firebaseOnAuthStateChanged;

export function getFirebaseAuthService() { return auth; }
export function getFirestoreService() { return db; }
export function getFunctionsService() { return functions; }

/**
 * Retrieves the current user's document from the 'players' collection.
 * @returns {Promise<object|null>} A promise that resolves to the player data object or null if not found/logged out.
 */
export async function getPlayerDocument() {
    const user = getCurrentUser();
    if (!user || !db) {
        console.log("getPlayerDocument: No user logged in or DB not available.");
        return null;
    }

    const playerRef = doc(db, "players", user.uid);
    try {
        const docSnap = await getDoc(playerRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn(`getPlayerDocument: No player document found for UID: ${user.uid}`);
            return null;
        }
    } catch (error) {
        console.error("Error getting player document:", error);
        return null;
    }
}

/**
 * Retrieves a player document by UID from the 'players' collection.
 * @param {string} uid The UID of the player to retrieve.
 * @returns {Promise<object|null>} A promise that resolves to the player data object or null if not found.
 */
export async function getPlayerDocumentById(uid) {
    if (!uid || !db) {
        console.log("getPlayerDocumentById: No UID provided or DB not available.");
        return null;
    }

    const playerRef = doc(db, "players", uid);
    try {
        const docSnap = await getDoc(playerRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn(`getPlayerDocumentById: No player document found for UID: ${uid}`);
            return null;
        }
    } catch (error) {
        console.error(`Error getting player document by ID (${uid}):`, error);
        return null;
    }
}

// --- Internal Functions (including setupAuthListenerInternal) ---
function setupAuthListenerInternal() {
    if (!auth) {
        console.error("Cannot setup auth listener: Auth service not initialized.");
        return;
    }
    console.log("Setting up Auth State Listener (Modular)...");
    firebaseOnAuthStateChanged(auth, (user) => {
        currentUserFromAuth = user;
        currentUserIdFromAuth = user ? user.uid : null;
        if (user) {
            console.log("Auth state changed: User logged in", currentUserFromAuth.displayName || currentUserIdFromAuth);
        } else {
            console.log("Auth state changed: User logged out");
            if (firestoreUnsubscribe) {
                console.log("Detaching Firestore listener on logout.");
                firestoreUnsubscribe();
                firestoreUnsubscribe = null;
            }
            currentGameIdFromAuth = null;
        }
        console.log("Dispatching krida-auth-state-changed event");
        document.dispatchEvent(new CustomEvent('krida-auth-state-changed', { detail: { user: currentUserFromAuth } }));
    });
}

// --- Example: Refactoring attachFirestoreListener --- 
export function attachFirestoreListener(gameId, callback, errorCallback) {
    if (!db || !gameId) {
        console.error("attachFirestoreListener: Firestore service or gameId not available.");
        if (errorCallback) errorCallback(new Error("DB or gameId missing"));
        return null;
    }
    console.log(`KridaAuth Module: Attaching Firestore listener for game ${gameId}`);
    const gameDocRef = doc(db, "games", gameId);
    try {
        const unsubscribe = onSnapshot(gameDocRef, (snapshot) => {
            console.log("KridaAuth Module: Firestore snapshot received.");
            if (callback) callback(snapshot);
        }, (err) => {
            console.error("KridaAuth Module: Firestore listener error:", err);
            if (errorCallback) errorCallback(err);
        });
        firestoreUnsubscribe = unsubscribe;
        return unsubscribe;
    } catch (err) {
        console.error("KridaAuth Module: Error setting up onSnapshot listener:", err);
        if (errorCallback) errorCallback(err);
        return null;
    }
}

console.log("KridaAuth module fully parsed and set up for exports."); 